import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { 
  Download, ShoppingBag, Music, CreditCard, 
  User, Settings, LogOut, FileAudio, Crown,
  ArrowLeft, ExternalLink, FileText, Clock,
  Calendar, Hash, Tag, ChevronRight, Package,
  Lock, Eye, EyeOff, Loader2, CheckCircle, Sparkles, Image, BookOpen
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Order Detail Panel ─── */
/* ─── Playlist Submission Component ─── */
const PlaylistSubmission = ({ orderId, itemName }) => {
  const [link, setLink] = useState('');
  const [platform, setPlatform] = useState('Spotify');
  const [clientNote, setClientNote] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const r = await axios.get(`${API_URL}/api/playlist/my-submissions`, { withCredentials: true });
        const existing = (r.data || []).find(s => s.order_id === orderId);
        if (existing) { setSubmitted(true); setStatus(existing.status); }
      } catch { }
    };
    check();
  }, [orderId]);

  const handleSubmit = async () => {
    if (!link.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/playlist/submit-link`, { order_id: orderId, platform_link: link, platform_name: platform, client_note: clientNote }, { withCredentials: true });
      setSubmitted(true);
      setStatus('pending');
    } catch (err) { alert(err.response?.data?.detail || 'Error enviando link'); }
    setSubmitting(false);
  };

  return (
    <div className="mt-8 pt-8 border-t border-white/10" data-testid="playlist-section">
      <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4 flex items-center gap-2">
        <Music className="w-4 h-4 text-[#E8720C]" />
        Ecosistema de Playlists
      </h3>
      {submitted ? (
        <div className="glass-card p-5 border border-[#E8720C]/10">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${status === 'in_playlist' ? 'bg-green-500' : 'bg-yellow-500 animate-pulse'}`} />
            <div>
              <p className="text-sm font-medium">
                {status === 'in_playlist' ? 'Tu musica ya esta en nuestras playlists!' : 'Link enviado - En revision'}
              </p>
              <p className="text-xs text-white/40">
                {status === 'in_playlist' 
                  ? 'Tu cancion esta sonando en el ecosistema de playlists de OG MUSIC. Ahora te toca ser grande!'
                  : 'Estamos revisando tu link. Te notificaremos por email cuando tu musica este en nuestras playlists.'}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="glass-card p-5 border border-white/5">
          <p className="text-sm text-white/60 mb-3">
            Este track incluye acceso al <strong className="text-[#E8720C]">Ecosistema de Playlists</strong>. 
            Cuando tu musica este en tu distribuidor favorito (Spotify, Apple Music, etc.), envianos el link y la incluimos en nuestras playlists principales.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-white/40 mb-1">Plataforma</label>
              <select value={platform} onChange={e => setPlatform(e.target.value)}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-[#E8720C]/50" data-testid="playlist-platform">
                <option value="Spotify">Spotify</option>
                <option value="Apple Music">Apple Music</option>
                <option value="YouTube Music">YouTube Music</option>
                <option value="Deezer">Deezer</option>
                <option value="Tidal">Tidal</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Link directo de tu musica</label>
              <input type="url" value={link} onChange={e => setLink(e.target.value)}
                placeholder="https://open.spotify.com/track/..."
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50" data-testid="playlist-link-input" />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Nota para el equipo (opcional)</label>
              <textarea value={clientNote} onChange={e => setClientNote(e.target.value)}
                placeholder="Ej: Lanzamiento el viernes, agregar a playlist de reggaeton..."
                rows={3}
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50 resize-none" data-testid="playlist-note-input" />
            </div>
            <button onClick={handleSubmit} disabled={submitting || !link.trim()}
              className="w-full py-3 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="playlist-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Music className="w-4 h-4" />}
              Enviar Link para Playlists
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const OrderDetail = ({ order, onBack, t, currentLang }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});
  const [generating, setGenerating] = useState(false);
  const [generatedCover, setGeneratedCover] = useState(null);
  const [coverChecked, setCoverChecked] = useState(false);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/user/order/${order.order_id}`, { withCredentials: true });
        setDetail(res.data);
        // Check if cover already exists
        try {
          const coverRes = await axios.get(`${API_URL}/api/covers/check/${order.order_id}`, { withCredentials: true });
          if (coverRes.data.exists) {
            setGeneratedCover({ cover_url: coverRes.data.cover_url });
          }
        } catch { }
        setCoverChecked(true);
      } catch {
        setDetail({ ...order, download_links: [], item_info: {} });
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [order]);

  const handleDownload = async (fileType) => {
    const orderId = detail?.order_id || order.order_id;
    setDownloading(prev => ({ ...prev, [fileType]: true }));
    try {
      const langParam = fileType === 'license' && currentLang ? `&lang=${currentLang}` : '';
      const res = await axios.get(
        `${API_URL}/api/orders/${orderId}/download?type=${fileType}${langParam}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const contentDisposition = res.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${orderId}.${fileType === 'license' ? 'pdf' : fileType}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      const errorData = err.response?.data;
      let msg;
      if (errorData instanceof Blob) {
        try { const text = await errorData.text(); const json = JSON.parse(text); msg = json.detail || json.error || 'Download failed'; } catch { msg = 'Download failed'; }
      } else {
        msg = errorData?.detail || errorData?.error || 'Download failed';
      }
      alert(msg);
    } finally {
      setDownloading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  const handleGenerateCover = async () => {
    const orderId = detail?.order_id || order.order_id;
    setGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/api/covers/generate`, { order_id: orderId }, { withCredentials: true });
      setGeneratedCover(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error generando portada');
    }
    setGenerating(false);
  };

  if (loading) {
    return (
      <div className="glass-card p-8">
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-white/5 rounded-lg animate-pulse" />)}
        </div>
      </div>
    );
  }

  const data = detail || order;
  const purchaseDate = data.purchased_at ? new Date(data.purchased_at) : null;

  return (
    <div className="space-y-6" data-testid="order-detail-view">
      <button onClick={onBack} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm" data-testid="order-detail-back">
        <ArrowLeft className="w-4 h-4" />
        {t('dashboard.back')}
      </button>

      <div className="glass-card p-8">
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8720C] mb-1">{t('dashboard.orderDetail')}</p>
            <h2 className="text-2xl font-bold">{data.item_name}</h2>
          </div>
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-500/15 text-green-400 border border-green-500/20">
            {t('dashboard.paid')}
          </span>
        </div>

        {/* Order info grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8 pb-8 border-b border-white/10">
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.orderId')}</p>
            <p className="font-mono text-sm">{data.display_id || data.order_id?.slice(0, 12)}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.licenseType')}</p>
            <p className="text-sm font-medium capitalize">{data.license_type || 'basic'}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.amount')}</p>
            <p className="text-sm font-bold text-[#E8720C]">${(data.amount || 0).toFixed(2)} USD</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.orderDate')}</p>
            <p className="text-sm">{purchaseDate ? purchaseDate.toLocaleDateString() : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.orderTime')}</p>
            <p className="text-sm">{purchaseDate ? purchaseDate.toLocaleTimeString() : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-white/40 uppercase tracking-wider mb-1">{t('dashboard.paymentMethod')}</p>
            <p className="text-sm capitalize">{data.payment_method || 'paypal'}</p>
          </div>
        </div>

        {/* Track/Pack info */}
        {data.item_info && Object.keys(data.item_info).length > 0 && (
          <div className="mb-8 pb-8 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">
              {data.item_type === 'pack' ? t('dashboard.packName') : t('dashboard.trackName')}
            </h3>
            <div className="p-4 bg-white/5 rounded-xl flex items-center gap-4">
              <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-[#E8720C]/20 to-[#E8720C]/5 flex items-center justify-center shrink-0">
                {data.item_type === 'pack' ? <Package className="w-6 h-6 text-[#E8720C]" /> : <Music className="w-6 h-6 text-[#E8720C]" />}
              </div>
              <div>
                <p className="font-semibold">{data.item_info.title || data.item_info.name || data.item_name}</p>
                {data.item_info.artist && <p className="text-sm text-white/50">by {data.item_info.artist}</p>}
                {data.item_info.genre && <p className="text-xs text-white/30 mt-1">{data.item_info.genre} {data.item_info.bpm ? `• ${data.item_info.bpm} BPM` : ''}</p>}
                {data.item_info.track_count && <p className="text-xs text-white/30 mt-1">{data.item_info.track_count} {t('dashboard.tracksIncluded').toLowerCase()}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Download buttons — secure backend-served */}
        <div>
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">{t('dashboard.allFiles')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* MP3 */}
            <button
              onClick={() => handleDownload('mp3')}
              disabled={downloading.mp3}
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-[#E8720C]/10 border border-white/10 hover:border-[#E8720C]/30 rounded-xl transition-all group text-left disabled:opacity-50"
              data-testid="download-mp3-btn">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Music className="w-5 h-5 text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{downloading.mp3 ? 'Downloading...' : t('dashboard.downloadMp3')}</p>
                <p className="text-xs text-white/30">Audio MP3</p>
              </div>
              <Download className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] transition-colors" />
            </button>

            {/* WAV */}
            <button
              onClick={() => handleDownload('wav')}
              disabled={downloading.wav}
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-[#E8720C]/10 border border-white/10 hover:border-[#E8720C]/30 rounded-xl transition-all group text-left disabled:opacity-50"
              data-testid="download-wav-btn">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <FileAudio className="w-5 h-5 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{downloading.wav ? 'Downloading...' : t('dashboard.downloadWav')}</p>
                <p className="text-xs text-white/30">Audio WAV</p>
              </div>
              <Download className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] transition-colors" />
            </button>

            {/* License PDF */}
            <button
              onClick={() => handleDownload('license')}
              disabled={downloading.license}
              className="flex items-center gap-3 p-4 bg-white/5 hover:bg-[#E8720C]/10 border border-white/10 hover:border-[#E8720C]/30 rounded-xl transition-all group text-left disabled:opacity-50"
              data-testid="download-pdf-btn">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{downloading.license ? 'Downloading...' : t('dashboard.downloadPdf')}</p>
                <p className="text-xs text-white/30">{t('dashboard.licensePdf')}</p>
              </div>
              <Download className="w-4 h-4 text-white/30 group-hover:text-[#E8720C] transition-colors" />
            </button>
          </div>
        </div>

        {/* AI Cover Generation — OG MUSIC */}
        <div className="mt-8 pt-8 border-t border-white/10">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#E8720C]" />
            Genera tu Portada OG Exclusiva
          </h3>
          {generatedCover ? (
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <img src={`${API_URL}${generatedCover.cover_url}`} alt="Cover"
                className="w-48 h-48 rounded-xl object-cover border border-white/10 shadow-lg" data-testid="generated-cover-preview" />
              <div className="flex-1">
                <p className="text-sm text-white/60 mb-2">Tu portada exclusiva para: <span className="text-white font-medium">{data.item_name}</span></p>
                <p className="text-xs text-green-400 mb-4 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Portada generada con exito</p>
                <a href={`${API_URL}${generatedCover.cover_url}`} download={`cover_${data.item_name}.png`}
                  className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-medium text-white inline-flex items-center gap-2 transition-colors" data-testid="download-cover-btn">
                  <Download className="w-4 h-4" />Descargar Portada
                </a>
              </div>
            </div>
          ) : coverChecked ? (
            <div className="flex items-center gap-4 p-4 bg-white/5 hover:bg-[#E8720C]/5 border border-white/10 hover:border-[#E8720C]/30 rounded-xl transition-all">
              <div className="w-14 h-14 rounded-lg bg-[#E8720C]/10 flex items-center justify-center shrink-0">
                <Image className="w-6 h-6 text-[#E8720C]" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Genera tu Portada OG</p>
                <p className="text-xs text-white/40 mt-0.5">Portada profesional exclusiva, estilo cinematico. 1 portada por cancion, solo para ti.</p>
              </div>
              <button onClick={handleGenerateCover} disabled={generating}
                className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-sm font-semibold text-white flex items-center gap-2 transition-colors disabled:opacity-50 shrink-0" data-testid="generate-cover-btn">
                {generating ? <><Loader2 className="w-4 h-4 animate-spin" />Generando...</> : <><Sparkles className="w-4 h-4" />Generar Portada OG</>}
              </button>
            </div>
          ) : null}
        </div>

        {/* Playlist Ecosystem — Submit Spotify Link */}
        {data.includes_playlist && (
          <PlaylistSubmission orderId={detail?.order_id || order.order_id} itemName={data.item_name} />
        )}
      </div>
    </div>
  );
};

/* ─── Empty State ─── */
const EmptyState = ({ icon: Icon, message, actionLabel, onAction }) => (
  <div className="text-center py-16">
    <Icon className="w-14 h-14 text-white/10 mx-auto mb-4" />
    <p className="text-white/40 mb-6">{message}</p>
    {actionLabel && (
      <button onClick={onAction} className="btn-secondary py-2.5 px-6 text-sm">{actionLabel}</button>
    )}
  </div>
);

/* ─── Purchase Row ─── */
const PurchaseRow = ({ purchase, onClick, t }) => {
  const date = purchase.purchased_at ? new Date(purchase.purchased_at) : null;
  return (
    <div 
      onClick={onClick}
      className="flex items-center justify-between p-4 bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-[#E8720C]/20 rounded-xl cursor-pointer transition-all group"
      data-testid={`purchase-row-${purchase.order_id}`}
    >
      <div className="flex items-center gap-4 min-w-0">
        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#E8720C]/15 to-[#E8720C]/5 flex items-center justify-center shrink-0">
          {purchase.item_type === 'pack' ? <Package className="w-5 h-5 text-[#E8720C]" /> : <Music className="w-5 h-5 text-[#E8720C]" />}
        </div>
        <div className="min-w-0">
          <p className="font-medium truncate">{purchase.item_name}</p>
          <div className="flex items-center gap-2 text-xs text-white/40 mt-1 flex-wrap">
            {purchase.license_type && (
              <span className="px-2 py-0.5 bg-white/5 rounded-md capitalize">{purchase.license_type}</span>
            )}
            {date && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {date.toLocaleDateString()}
              </span>
            )}
            {date && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Hash className="w-3 h-3" />
              {(purchase.display_id || purchase.order_id || '').slice(0, 12)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4 shrink-0">
        <span className="text-sm font-bold text-[#E8720C]">${(purchase.amount || 0).toFixed(2)}</span>
        {purchase.replacement_status === 'under_verification' ? (
          <span className="px-2 py-1 text-[10px] font-semibold rounded-full bg-yellow-500/10 text-yellow-400" data-testid="replacement-status-badge">En verificacion</span>
        ) : purchase.replacement_status === 'completed' ? (
          <span className="px-2 py-1 text-[10px] font-semibold rounded-full bg-blue-500/10 text-blue-400" data-testid="replacement-status-badge">Reemplazado</span>
        ) : (
          <span className="px-2 py-1 text-[10px] font-semibold rounded-full bg-green-500/10 text-green-400">{t('dashboard.paid')}</span>
        )}
        <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#E8720C] transition-colors" />
      </div>
    </div>
  );
};

/* ─── Download Item ─── */
const DownloadItem = ({ item, t, currentLang }) => {
  const [downloading, setDownloading] = useState({});

  const handleDownload = async (fileType) => {
    setDownloading(prev => ({ ...prev, [fileType]: true }));
    try {
      const langParam = fileType === 'license' && currentLang ? `&lang=${currentLang}` : '';
      const res = await axios.get(
        `${API_URL}/api/orders/${item.order_id}/download?type=${fileType}${langParam}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const contentDisposition = res.headers['content-disposition'] || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${item.order_id}.${fileType === 'license' ? 'pdf' : fileType}`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      const errorData = err.response?.data;
      let msg;
      if (errorData instanceof Blob) {
        try { const text = await errorData.text(); const json = JSON.parse(text); msg = json.detail || json.error || 'Download failed'; } catch { msg = 'Download failed'; }
      } else {
        msg = errorData?.detail || errorData?.error || 'Download failed';
      }
      alert(msg);
    } finally {
      setDownloading(prev => ({ ...prev, [fileType]: false }));
    }
  };

  return (
    <div className="p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl" data-testid={`download-item-${item.order_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#E8720C]/10 flex items-center justify-center shrink-0">
            {item.item_type === 'pack' ? <Package className="w-4 h-4 text-[#E8720C]" /> : <Music className="w-4 h-4 text-[#E8720C]" />}
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm truncate">{item.item_name}</p>
            <p className="text-xs text-white/30 capitalize">{item.license_type} license</p>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => handleDownload('mp3')} disabled={downloading.mp3}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          data-testid="dl-mp3">
          <Download className="w-3 h-3" /> {downloading.mp3 ? '...' : 'MP3'}
        </button>
        <button onClick={() => handleDownload('wav')} disabled={downloading.wav}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          data-testid="dl-wav">
          <Download className="w-3 h-3" /> {downloading.wav ? '...' : 'WAV'}
        </button>
        <button onClick={() => handleDownload('license')} disabled={downloading.license}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          data-testid="dl-pdf">
          <FileText className="w-3 h-3" /> {downloading.license ? '...' : t('dashboard.licensePdf')}
        </button>
      </div>
    </div>
  );
};

/* ─── Main Dashboard ─── */
const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, logout } = useAuth();
  const { t, language: currentLang } = useLanguage();
  const [purchases, setPurchases] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('purchases');
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchUserData();
  }, [user, authLoading, navigate]);

  const fetchUserData = async () => {
    try {
      const [pRes, dRes] = await Promise.all([
        axios.get(`${API_URL}/api/user/purchases`, { withCredentials: true }),
        axios.get(`${API_URL}/api/user/downloads`, { withCredentials: true })
      ]);
      setPurchases(pRes.data);
      setDownloads(dRes.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/'); };

  const trackPurchases = purchases.filter(p => p.item_type === 'track');
  const packPurchases = purchases.filter(p => p.item_type === 'pack');
  const trackDownloads = downloads.filter(d => d.item_type === 'track');
  const packDownloads = downloads.filter(d => d.item_type === 'pack');
  // Distribution Guide is EXCLUSIVE to Ultimate Pack buyers
  const hasUltimatePack = purchases.some(p => p.item_type === 'pack' && p.item_id === 'ultimate');

  const stats = [
    { id: 'purchases', icon: ShoppingBag, label: t('dashboard.purchases'), value: purchases.length, tab: 'purchases' },
    { id: 'downloads', icon: Download, label: t('dashboard.downloads'), value: downloads.length, tab: 'downloads' },
    { id: 'tracks', icon: Music, label: t('dashboard.tracks'), value: trackPurchases.length, tab: 'tracks' },
    { id: 'packs', icon: Package, label: t('dashboard.packs'), value: packPurchases.length, tab: 'packs' },
  ];

  const tabs = [
    { id: 'purchases', icon: ShoppingBag, label: t('dashboard.purchases') },
    { id: 'downloads', icon: Download, label: t('dashboard.downloads') },
    { id: 'tracks', icon: Music, label: t('dashboard.tracks') },
    { id: 'packs', icon: Package, label: t('dashboard.packs') },
    ...(hasUltimatePack ? [{ id: 'guide', icon: BookOpen, label: 'Guia de Distribucion' }] : []),
    { id: 'settings', icon: Settings, label: t('dashboard.settings') },
  ];

  if (authLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#E8720C]" />
    </div>
  );

  if (!user) return null;

  // If viewing order detail
  if (selectedOrder) {
    return (
      <div className="min-h-screen pt-24">
        <div className="container-custom py-12 max-w-4xl">
          <OrderDetail order={selectedOrder} onBack={() => setSelectedOrder(null)} t={t} currentLang={currentLang} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="customer-dashboard">
      <div className="container-custom py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/40 mb-2">{t('dashboard.welcomeBack')}</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{user.name}</h1>
            <p className="text-white/40 mt-1 text-sm">{user.email}</p>
          </div>
          <div className="mt-6 md:mt-0 flex gap-3">
            {user.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="btn-gold py-2 px-5 flex items-center gap-2 text-sm" data-testid="admin-panel-btn">
                <Crown className="w-4 h-4" /> Admin Panel
              </button>
            )}
            <button onClick={handleLogout} className="btn-secondary py-2 px-5 flex items-center gap-2 text-sm" data-testid="dashboard-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Stat Cards — clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {stats.map((stat) => (
            <button
              key={stat.id}
              onClick={() => setActiveTab(stat.tab)}
              className={`glass-card p-6 text-center transition-all hover:border-[#E8720C]/30 cursor-pointer ${activeTab === stat.tab ? 'border-[#E8720C]/40 bg-[#E8720C]/5' : ''}`}
              data-testid={`stat-card-${stat.id}`}
            >
              <stat.icon className="w-6 h-6 text-[#E8720C] mx-auto mb-3" />
              <p className="text-2xl font-bold">{loading ? '—' : stat.value}</p>
              <p className="text-xs text-white/40 uppercase tracking-wide mt-1">{stat.label}</p>
            </button>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-1 p-1 bg-white/[0.03] border border-white/[0.06] rounded-xl mb-8 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white/60'}`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* ═══ PURCHASES TAB ═══ */}
        {activeTab === 'purchases' && (
          <div className="glass-card p-6" data-testid="tab-content-purchases">
            <h2 className="text-lg font-semibold mb-1">{t('dashboard.purchaseHistory')}</h2>
            <p className="text-xs text-white/30 mb-6">{t('dashboard.completedOrders')}</p>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />)}</div>
            ) : purchases.length === 0 ? (
              <EmptyState icon={ShoppingBag} message={t('dashboard.noPurchases')} actionLabel={t('dashboard.browseCatalog')} onAction={() => navigate('/catalog')} />
            ) : (
              <div className="space-y-3">
                {purchases.map((p, i) => (
                  <PurchaseRow key={i} purchase={p} onClick={() => setSelectedOrder(p)} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ DOWNLOADS TAB ═══ */}
        {activeTab === 'downloads' && (
          <div className="glass-card p-6" data-testid="tab-content-downloads">
            <h2 className="text-lg font-semibold mb-1">{t('dashboard.yourDownloads')}</h2>
            <p className="text-xs text-white/30 mb-6">{t('dashboard.allFiles')}</p>
            {downloads.length === 0 ? (
              <EmptyState icon={Download} message={t('dashboard.noDownloads')} actionLabel={t('dashboard.browseCatalog')} onAction={() => navigate('/catalog')} />
            ) : (
              <div className="space-y-6">
                {/* Track downloads */}
                {trackDownloads.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Music className="w-4 h-4" /> {t('dashboard.tracks')}
                    </h3>
                    <div className="space-y-2">
                      {trackDownloads.map((d, i) => <DownloadItem key={i} item={d} t={t} currentLang={currentLang} />)}
                    </div>
                  </div>
                )}
                {/* Pack downloads */}
                {packDownloads.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-white/50 uppercase tracking-wider mb-3 flex items-center gap-2">
                      <Package className="w-4 h-4" /> {t('dashboard.packs')}
                    </h3>
                    <div className="space-y-2">
                      {packDownloads.map((d, i) => <DownloadItem key={i} item={d} t={t} currentLang={currentLang} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ TRACKS TAB ═══ */}
        {activeTab === 'tracks' && (
          <div className="glass-card p-6" data-testid="tab-content-tracks">
            <h2 className="text-lg font-semibold mb-1">{t('dashboard.yourTracks')}</h2>
            <p className="text-xs text-white/30 mb-6">{t('dashboard.tracks')}</p>
            {trackPurchases.length === 0 ? (
              <EmptyState icon={Music} message={t('dashboard.noTracks')} actionLabel={t('dashboard.browseCatalog')} onAction={() => navigate('/catalog')} />
            ) : (
              <div className="space-y-3">
                {trackPurchases.map((p, i) => (
                  <PurchaseRow key={i} purchase={p} onClick={() => setSelectedOrder(p)} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ PACKS TAB ═══ */}
        {activeTab === 'packs' && (
          <div className="glass-card p-6" data-testid="tab-content-packs">
            <h2 className="text-lg font-semibold mb-1">{t('dashboard.yourPacks')}</h2>
            <p className="text-xs text-white/30 mb-6">{t('dashboard.packs')}</p>
            {packPurchases.length === 0 ? (
              <EmptyState icon={Package} message={t('dashboard.noPacks')} actionLabel={t('dashboard.browseCatalog')} onAction={() => navigate('/catalog')} />
            ) : (
              <div className="space-y-3">
                {packPurchases.map((p, i) => (
                  <PurchaseRow key={i} purchase={p} onClick={() => setSelectedOrder(p)} t={t} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {activeTab === 'settings' && <SettingsTab user={user} t={t} />}

        {/* ═══ DISTRIBUTION GUIDE TAB (Ultimate Pack only) ═══ */}
        {activeTab === 'guide' && hasUltimatePack && <DistributionGuideTab />}
      </div>
    </div>
  );
};

/* ─── Settings Tab Component ─── */
const SettingsTab = ({ user, t }) => {
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState('');

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess(false);
    if (pwForm.newPw.length < 8) { setPwError(t('auth.passwordMinLength') || 'Password must be at least 8 characters'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError(t('auth.passwordsMustMatch') || 'Passwords do not match'); return; }
    setPwLoading(true);
    try {
      await axios.post(`${API_URL}/api/user/change-password`, { current_password: pwForm.current, new_password: pwForm.newPw }, { withCredentials: true });
      setPwSuccess(true);
      setPwForm({ current: '', newPw: '', confirm: '' });
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="tab-content-settings">
      {/* Account Info (Read-Only) */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold mb-6">{t('dashboard.accountSettings') || 'Account Settings'}</h2>
        <div className="flex items-center gap-4 p-5 bg-white/[0.03] border border-white/[0.06] rounded-xl">
          <div className="w-12 h-12 rounded-full bg-[#E8720C]/10 flex items-center justify-center">
            <User className="w-6 h-6 text-[#E8720C]" />
          </div>
          <div className="flex-1">
            <p className="font-semibold" data-testid="settings-user-name">{user.name}</p>
            <p className="text-sm text-white/40" data-testid="settings-user-email">{user.email}</p>
          </div>
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-[#E8720C]/15 text-[#E8720C]' : 'bg-white/10 text-white/50'}`}>
            {user.role}
          </span>
        </div>
        <p className="text-[11px] text-white/30 mt-3 ml-1">
          {t('dashboard.nameLockedNote') || 'Name and email are locked after registration. Contact support to request changes.'}
        </p>
      </div>

      {/* Security Settings — Change Password */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
            <Lock className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <h3 className="font-semibold">{t('dashboard.securitySettings') || 'Security Settings'}</h3>
            <p className="text-xs text-white/40">{t('dashboard.changeYourPassword') || 'Change your account password'}</p>
          </div>
        </div>

        {pwSuccess && (
          <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 text-sm mb-4" data-testid="pw-change-success">
            <CheckCircle className="w-4 h-4" /> {t('dashboard.passwordChanged') || 'Password changed successfully!'}
          </div>
        )}
        {pwError && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm mb-4" data-testid="pw-change-error">
            {pwError}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-xs text-white/50 mb-1">{t('dashboard.currentPassword') || 'Current Password'}</label>
            <input type={showPw ? 'text' : 'password'} value={pwForm.current} onChange={e => setPwForm(f => ({...f, current: e.target.value}))} required
              placeholder="Enter current password"
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
              data-testid="portal-current-pw" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">{t('dashboard.newPasswordLabel') || 'New Password (min 8 characters)'}</label>
            <input type={showPw ? 'text' : 'password'} value={pwForm.newPw} onChange={e => setPwForm(f => ({...f, newPw: e.target.value}))} required minLength={8}
              placeholder="Enter new password"
              className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
              data-testid="portal-new-pw" />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">{t('dashboard.confirmNewPassword') || 'Confirm New Password'}</label>
            <div className="relative">
              <input type={showPw ? 'text' : 'password'} value={pwForm.confirm} onChange={e => setPwForm(f => ({...f, confirm: e.target.value}))} required minLength={8}
                placeholder="Confirm new password"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50 pr-10"
                data-testid="portal-confirm-pw" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {pwForm.newPw && pwForm.confirm && pwForm.newPw !== pwForm.confirm && (
              <p className="text-red-400 text-xs mt-1">{t('auth.passwordsMustMatch') || 'Passwords do not match'}</p>
            )}
          </div>
          <button type="submit" disabled={pwLoading} className="px-6 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white flex items-center gap-2 disabled:opacity-50" data-testid="portal-change-pw-btn">
            {pwLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : (t('dashboard.changePasswordBtn') || 'Change Password')}
          </button>
        </form>
      </div>

      {/* Billing — Payment Preferences */}
      <BillingSection t={t} />
    </div>
  );
};

/* ─── Billing Section Component (PayPal Secure) ─── */
const BillingSection = ({ t }) => {
  const [methods, setMethods] = useState([]);
  const [defaultMethod, setDefaultMethod] = useState(null);
  const [autoPay, setAutoPay] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [formType, setFormType] = useState('paypal');
  const [form, setForm] = useState({ paypal_email: '', crypto_currency: 'USDT', wallet_address: '' });

  // PayPal card fields state
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalClientToken, setPaypalClientToken] = useState('');
  const [cardLoading, setCardLoading] = useState(false);
  const [cardError, setCardError] = useState('');

  useEffect(() => {
    loadBilling();
  }, []);

  const loadBilling = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/user/billing`, { withCredentials: true });
      setMethods(res.data.methods || []);
      setDefaultMethod(res.data.default_method);
      setAutoPay(res.data.auto_pay || false);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const initPayPalCardFields = async () => {
    setCardLoading(true);
    setCardError('');
    try {
      const [idRes, tokenRes] = await Promise.all([
        axios.get(`${API_URL}/api/checkout/paypal-client-id`),
        axios.get(`${API_URL}/api/checkout/paypal-client-token`, { withCredentials: true }),
      ]);
      setPaypalClientId(idRes.data.client_id || '');
      setPaypalClientToken(tokenRes.data.client_token || '');
    } catch (err) {
      console.error('PayPal init error:', err);
      setCardError(t('dashboard.paypalCardError') || 'Could not initialize PayPal card entry. Ensure PayPal is configured in Admin Settings.');
    } finally {
      setCardLoading(false);
    }
  };

  const handleSelectCardType = () => {
    setFormType('card');
    setShowForm(true);
    setEditIndex(null);
    initPayPalCardFields();
  };

  const handleSaveNonCard = async () => {
    setSaving(true);
    try {
      const newMethods = [...methods];
      let entry;
      if (formType === 'paypal') {
        entry = { type: 'paypal', paypal_email: form.paypal_email };
      } else {
        entry = { type: 'crypto', crypto_currency: form.crypto_currency, wallet_address: form.wallet_address };
      }
      if (editIndex !== null) { newMethods[editIndex] = entry; }
      else { newMethods.push(entry); }
      const newDefault = defaultMethod || `${entry.type}-0`;
      await axios.post(`${API_URL}/api/user/billing`, { methods: newMethods, default_method: newDefault, auto_pay: autoPay }, { withCredentials: true });
      setShowForm(false);
      setEditIndex(null);
      setForm({ paypal_email: '', crypto_currency: 'USDT', wallet_address: '' });
      loadBilling();
    } catch (err) { alert('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (idx) => {
    const newMethods = methods.filter((_, i) => i !== idx);
    await axios.post(`${API_URL}/api/user/billing`, { methods: newMethods, default_method: defaultMethod, auto_pay: autoPay }, { withCredentials: true });
    loadBilling();
  };

  const handleSetDefault = async (key) => {
    await axios.post(`${API_URL}/api/user/billing`, { methods, default_method: key, auto_pay: autoPay }, { withCredentials: true });
    setDefaultMethod(key);
  };

  const toggleAutoPay = async () => {
    const newVal = !autoPay;
    setAutoPay(newVal);
    await axios.post(`${API_URL}/api/user/billing`, { methods, default_method: defaultMethod, auto_pay: newVal }, { withCredentials: true });
  };

  const startEdit = (idx) => {
    const m = methods[idx];
    if (m.type === 'card') return; // Cannot edit vaulted cards — delete and re-add
    setFormType(m.type);
    setForm({
      paypal_email: m.paypal_email || '',
      crypto_currency: m.crypto_currency || 'USDT',
      wallet_address: m.wallet_address || '',
    });
    setEditIndex(idx);
    setShowForm(true);
  };

  const getMethodIcon = (m) => {
    if (m.type === 'card') return 'CARD';
    if (m.type === 'paypal') return 'PP';
    return m.crypto_currency === 'BTC' ? 'BTC' : 'USDT';
  };

  const getMethodLabel = (m) => {
    if (m.type === 'card') return `${m.card_brand || 'Card'} ****${m.last4}`;
    if (m.type === 'paypal') return 'PayPal';
    return `Crypto (${m.crypto_currency})`;
  };

  const getMethodDetail = (m) => {
    if (m.type === 'card') return m.expiration ? `Exp: ${m.expiration}` : (t('dashboard.securedViaPayPal') || 'Secured via PayPal');
    if (m.type === 'paypal') return m.paypal_email;
    return m.wallet_address || 'No wallet saved';
  };

  if (loading) return null;

  return (
    <div className="glass-card p-6" data-testid="billing-section">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E8720C]/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-[#E8720C]" />
          </div>
          <div>
            <h3 className="font-semibold">{t('dashboard.billing') || 'Billing'}</h3>
            <p className="text-xs text-white/40">{t('dashboard.manageBilling') || 'Manage your payment methods'}</p>
          </div>
        </div>
        {!showForm && (
          <button onClick={() => { setEditIndex(null); setFormType('paypal'); setForm({ paypal_email: '', crypto_currency: 'USDT', wallet_address: '' }); setShowForm(true); }}
            className="px-4 py-2 bg-[#E8720C] hover:bg-[#C55A00] rounded-lg text-xs font-semibold text-white" data-testid="billing-add-btn">
            {methods.length === 0 ? (t('dashboard.addPaymentMethod') || 'Add Payment Method') : (t('dashboard.addAnother') || 'Add Another')}
          </button>
        )}
      </div>

      {methods.length === 0 && !showForm && (
        <div className="flex items-center gap-3 text-white/40 text-sm py-4">
          <CreditCard className="w-5 h-5" />
          <span>{t('dashboard.noPaymentMethods') || 'No payment methods saved'}</span>
        </div>
      )}

      {methods.map((m, idx) => {
        const key = `${m.type}-${idx}`;
        const isDefault = defaultMethod === key;
        return (
          <div key={idx} className="flex items-center gap-4 p-4 bg-white/[0.03] border border-white/[0.06] rounded-xl mb-3" data-testid={`billing-method-${idx}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${m.type === 'paypal' ? 'bg-blue-500/10 text-blue-400' : m.type === 'card' ? 'bg-purple-500/10 text-purple-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
              {getMethodIcon(m)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{getMethodLabel(m)}</p>
              <p className="text-xs text-white/40 truncate">{getMethodDetail(m)}</p>
            </div>
            <div className="flex items-center gap-2">
              {isDefault ? (
                <span className="px-2.5 py-1 bg-green-500/15 text-green-400 text-[10px] font-bold rounded-md" data-testid={`billing-default-${idx}`}>DEFAULT</span>
              ) : (
                <button onClick={() => handleSetDefault(key)} className="px-2.5 py-1 bg-white/5 text-white/40 text-[10px] rounded-md hover:bg-white/10" data-testid={`billing-set-default-${idx}`}>
                  {t('dashboard.setDefault') || 'Set Default'}
                </button>
              )}
              {m.type !== 'card' && (
                <button onClick={() => startEdit(idx)} className="px-2.5 py-1 bg-white/5 text-white/40 text-[10px] rounded-md hover:bg-white/10" data-testid={`billing-edit-${idx}`}>
                  {t('dashboard.edit') || 'Edit'}
                </button>
              )}
              <button onClick={() => handleDelete(idx)} className="px-2.5 py-1 bg-red-500/10 text-red-400/60 text-[10px] rounded-md hover:bg-red-500/20 hover:text-red-400" data-testid={`billing-delete-${idx}`}>
                {t('dashboard.delete') || 'Delete'}
              </button>
            </div>
          </div>
        );
      })}

      {/* Auto-Pay Toggle */}
      {methods.length > 0 && (
        <div className="flex items-center justify-between p-4 bg-white/[0.02] border border-white/[0.06] rounded-xl mt-4" data-testid="auto-pay-section">
          <div>
            <p className="text-sm font-medium">{t('dashboard.autoPay') || 'Auto Pay'}</p>
            <p className="text-[11px] text-white/40">{t('dashboard.autoPayDesc') || 'Automatically charge default method for memberships'}</p>
          </div>
          <button onClick={toggleAutoPay} className={`w-12 h-7 rounded-full transition-colors relative ${autoPay ? 'bg-[#E8720C]' : 'bg-white/10'}`} data-testid="auto-pay-toggle">
            <div className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${autoPay ? 'left-6' : 'left-1'}`} />
          </button>
        </div>
      )}

      {/* Add/Edit form */}
      {showForm && (
        <div className="p-5 bg-white/[0.03] border border-[#E8720C]/20 rounded-xl mt-3 space-y-4" data-testid="billing-form">
          <div className="flex gap-2">
            {['paypal', 'card', 'crypto'].map(type => (
              <button key={type}
                onClick={() => {
                  if (type === 'card') { handleSelectCardType(); }
                  else { setFormType(type); }
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formType === type ? 'bg-[#E8720C]/20 text-[#E8720C] border border-[#E8720C]/30' : 'bg-white/5 text-white/50 border border-white/10'}`}
                data-testid={`billing-type-${type}`}>
                {type === 'paypal' ? 'PayPal' : type === 'card' ? 'Card' : 'Crypto'}
              </button>
            ))}
          </div>

          {formType === 'paypal' && (
            <div>
              <label className="block text-xs text-white/50 mb-1">{t('dashboard.paypalEmail') || 'PayPal Email'}</label>
              <input type="email" value={form.paypal_email} onChange={e => setForm(f => ({...f, paypal_email: e.target.value}))}
                placeholder="your@paypal.com"
                className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                data-testid="billing-paypal-email" />
            </div>
          )}

          {formType === 'card' && (
            <PayPalCardEntry
              clientId={paypalClientId}
              clientToken={paypalClientToken}
              cardLoading={cardLoading}
              cardError={cardError}
              onCardSaved={() => { setShowForm(false); loadBilling(); }}
              t={t}
            />
          )}

          {formType === 'crypto' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-white/50 mb-1">{t('dashboard.cryptoCurrency') || 'Cryptocurrency'}</label>
                <div className="flex gap-2">
                  {['USDT', 'BTC'].map(c => (
                    <button key={c} onClick={() => setForm(f => ({...f, crypto_currency: c}))}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${form.crypto_currency === c ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`}
                      data-testid={`billing-crypto-${c.toLowerCase()}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/50 mb-1">{t('dashboard.walletAddress') || 'Wallet Address (optional)'}</label>
                <input value={form.wallet_address} onChange={e => setForm(f => ({...f, wallet_address: e.target.value}))}
                  placeholder="0x... or wallet address"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#E8720C]/50"
                  data-testid="billing-wallet-address" />
              </div>
            </div>
          )}

          {formType !== 'card' && (
            <div className="flex gap-3">
              <button onClick={handleSaveNonCard} disabled={saving || (formType === 'paypal' && !form.paypal_email)}
                className="px-5 py-2.5 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white disabled:opacity-50" data-testid="billing-save-btn">
                {saving ? 'Saving...' : (editIndex !== null ? (t('dashboard.update') || 'Update') : (t('dashboard.save') || 'Save'))}
              </button>
              <button onClick={() => { setShowForm(false); setEditIndex(null); }}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-sm text-white/60" data-testid="billing-cancel-btn">
                {t('dashboard.cancel') || 'Cancel'}
              </button>
            </div>
          )}
          {formType === 'card' && (
            <div className="flex gap-3">
              <button onClick={() => { setShowForm(false); setEditIndex(null); }}
                className="px-5 py-2.5 bg-white/10 hover:bg-white/15 rounded-xl text-sm text-white/60" data-testid="billing-cancel-btn">
                {t('dashboard.cancel') || 'Cancel'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── PayPal Hosted Card Entry Component ─── */
const PayPalCardEntry = ({ clientId, clientToken, cardLoading, cardError, onCardSaved, t }) => {
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [sdkReady, setSdkReady] = useState(false);
  const [notEligible, setNotEligible] = useState(false);
  const paypalRef = React.useRef(null);

  useEffect(() => {
    if (!clientId || !clientToken || cardLoading) return;

    const existingScript = document.querySelector('script[data-paypal-card-fields]');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&components=hosted-fields&currency=USD`;
    script.setAttribute('data-client-token', clientToken);
    script.setAttribute('data-paypal-card-fields', 'true');
    script.async = true;

    script.onload = () => {
      if (window.paypal && window.paypal.HostedFields) {
        initHostedFields();
      } else {
        // Not eligible — show clean message, not a technical error
        setNotEligible(true);
      }
    };
    script.onerror = () => {
      setNotEligible(true);
    };

    document.body.appendChild(script);

    return () => {
      const s = document.querySelector('script[data-paypal-card-fields]');
      if (s) s.remove();
    };
  }, [clientId, clientToken, cardLoading]);

  const initHostedFields = async () => {
    try {
      if (!window.paypal?.HostedFields?.isEligible()) {
        setNotEligible(true);
        return;
      }

      const hf = await window.paypal.HostedFields.render({
        createOrder: async () => {
          const res = await axios.post(`${API_URL}/api/checkout/create-order`, {
            item_type: 'vault',
            item_id: 'card-vault',
            origin_url: window.location.origin,
          }, { withCredentials: true });
          return res.data.order_id;
        },
        styles: {
          input: {
            color: '#ffffff',
            'font-size': '14px',
            'font-family': 'system-ui, -apple-system, sans-serif',
          },
          '.invalid': { color: '#ef4444' },
          ':focus': { color: '#ffffff' },
        },
        fields: {
          number: { selector: '#paypal-card-number', placeholder: '4111 1111 1111 1111' },
          cvv: { selector: '#paypal-cvv', placeholder: '123' },
          expirationDate: { selector: '#paypal-expiry', placeholder: 'MM/YY' },
        },
      });

      paypalRef.current = hf;
      setSdkReady(true);
    } catch (err) {
      console.error('PayPal HostedFields init error:', err);
      setNotEligible(true);
    }
  };

  const handleSubmitCard = async () => {
    if (!paypalRef.current) return;
    setSubmitting(true);
    setLocalError('');
    try {
      const result = await paypalRef.current.submit({});
      const cardDetails = result?.paymentSource?.card || {};

      await axios.post(`${API_URL}/api/checkout/vault-card`, {
        order_id: result.orderId || result.orderID || '',
        card: {
          brand: cardDetails.brand || cardDetails.card_type || 'Card',
          last_digits: cardDetails.last_digits || cardDetails.lastDigits || '',
          expiry: cardDetails.expiry || '',
          type: cardDetails.type || '',
        },
      }, { withCredentials: true });

      onCardSaved();
    } catch (err) {
      console.error('Card submit error:', err);
      setLocalError(t('dashboard.cardSaveError') || 'Failed to save card. Please check the details and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (cardLoading) {
    return (
      <div className="flex items-center gap-3 py-6 justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-[#E8720C]" />
        <span className="text-sm text-white/50">{t('dashboard.initializingPayPal') || 'Initializing secure card entry...'}</span>
      </div>
    );
  }

  // Professional not-eligible message — no technical errors shown
  if (cardError || notEligible) {
    return (
      <div className="space-y-4" data-testid="card-not-available">
        <div className="p-5 bg-white/[0.03] border border-white/[0.08] rounded-xl text-center">
          <CreditCard className="w-8 h-8 text-white/20 mx-auto mb-3" />
          <p className="text-sm text-white/70 font-medium mb-1">
            {t('dashboard.cardNotAvailable') || 'Card payments are not currently available for this account.'}
          </p>
          <p className="text-xs text-white/40">
            {t('dashboard.useAlternative') || 'Please use PayPal or Crypto as your payment method. You can also pay with a card directly at checkout through PayPal.'}
          </p>
        </div>
      </div>
    );
  }

  if (localError) {
    return (
      <div className="space-y-3">
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="card-entry-error">
          {localError}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="paypal-card-entry">
      <div className="flex items-center gap-2 mb-2">
        <Lock className="w-4 h-4 text-green-400" />
        <span className="text-xs text-green-400/80">{t('dashboard.secureCardNote') || 'Card data is handled securely by PayPal. We never see or store your full card number or CVV.'}</span>
      </div>

      <div>
        <label className="block text-xs text-white/50 mb-1">{t('dashboard.cardNumber') || 'Card Number'}</label>
        <div id="paypal-card-number" className="w-full h-[44px] px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="paypal-card-number-field" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-white/50 mb-1">{t('dashboard.expiryDate') || 'Expiry Date'}</label>
          <div id="paypal-expiry" className="w-full h-[44px] px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="paypal-expiry-field" />
        </div>
        <div>
          <label className="block text-xs text-white/50 mb-1">CVV</label>
          <div id="paypal-cvv" className="w-full h-[44px] px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl" data-testid="paypal-cvv-field" />
        </div>
      </div>

      <button
        onClick={handleSubmitCard}
        disabled={submitting || !sdkReady}
        className="w-full py-3 bg-[#E8720C] hover:bg-[#C55A00] rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="paypal-card-submit-btn"
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> {t('dashboard.savingCard') || 'Saving card...'}</>
        ) : (
          <><Lock className="w-4 h-4" /> {t('dashboard.saveCardSecurely') || 'Save Card Securely via PayPal'}</>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 pt-1">
        <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png" alt="PayPal" className="h-4 opacity-50" />
        <span className="text-[10px] text-white/30">{t('dashboard.poweredByPayPal') || 'Powered by PayPal Secure Card Processing'}</span>
      </div>
    </div>
  );
};

export default DashboardPage;

/* ─── Distribution Guide Tab (only for buyers) ─── */
const guideDistributors = [
  { name: 'Amuse', price: 'GRATIS', note: 'Pro: $5.99/mes', color: '#4ade80', best: 'Empezar sin gastar',
    pros: ['Sin costo inicial', 'Todas las plataformas', 'App facil'], url: 'https://amuse.io' },
  { name: 'DistroKid', price: '$22.99/año', note: 'Subidas ilimitadas', color: '#E8720C', best: 'Artistas activos',
    pros: ['Ilimitado por año', 'La mas rapida', 'Splits gratis'], url: 'https://distrokid.com' },
  { name: 'TuneCore', price: '$9.99/single', note: 'Album: $29.99/año', color: '#3b82f6', best: 'Pocos lanzamientos',
    pros: ['100% regalias', 'Marketing tools', 'Publishing admin'], url: 'https://tunecore.com' },
  { name: 'CD Baby', price: '$9.95 (unico)', note: 'No pagas cada año', color: '#a855f7', best: 'Pago unico',
    pros: ['Pago una vez', 'Sync licensing', 'YouTube Content ID'], url: 'https://cdbaby.com' },
  { name: 'UnitedMasters', price: 'GRATIS', note: 'Pro: $59.99/año', color: '#ef4444', best: 'Urbano/Hip-hop',
    pros: ['Deals con marcas', '100% ownership', 'Popular urbano'], url: 'https://unitedmasters.com' },
];

const guideTips = [
  { title: 'Sube 2-3 semanas antes', desc: 'Spotify tarda 3-7 dias en procesar. Sube el lunes anterior a tu viernes de lanzamiento.' },
  { title: 'Portada 3000x3000px', desc: 'JPG o PNG, sin texto borroso, sin logos de plataformas. Si no cumple, te la rechazan.' },
  { title: 'Formato WAV 16-bit 44.1kHz', desc: 'Calidad CD obligatoria. No subas MP3 a la distribuidora.' },
  { title: 'Reclama tu perfil', desc: 'Spotify for Artists y Apple Music for Artists te dan analytics y control de tu perfil.' },
  { title: 'Registra en ASCAP/BMI', desc: 'Sin registro en una PRO pierdes regalias cada vez que suena tu musica en radio o TV.' },
  { title: 'ISRC y UPC obligatorios', desc: 'Identifican tu cancion y album. La distribuidora los genera gratis.' },
];

const DistributionGuideTab = () => {
  const [info, setInfo] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/distribution-guide/eligibility`, { withCredentials: true });
        setInfo(r.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API_URL}/api/distribution-guide/download`,
        { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'OG_Distribution_Guide.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error descargando guía');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="tab-content-guide">
      {/* PDF download hero (shown when guide is ready) */}
      <div className="glass-card p-8 text-center" style={{ background: 'linear-gradient(135deg, rgba(232,114,12,0.10), rgba(197,90,0,0.04))' }}>
        <p className="text-[#E8720C] text-[10px] font-bold uppercase tracking-[3px] mb-2">Exclusivo Ultimate Pack</p>
        <h2 className="text-2xl font-bold mb-2">Tu Guia de Distribucion Privada</h2>
        <p className="text-white/50 text-sm max-w-lg mx-auto mb-6">PDF paso-a-paso para subir tu musica a Spotify, Apple Music y mas de 150 plataformas.</p>
        {info?.guide_available ? (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#E8720C] hover:bg-[#C55A00] rounded-full font-bold text-sm disabled:opacity-50"
            data-testid="download-guide-btn"
          >
            {downloading ? '⏳ Descargando…' : '📄 Descargar PDF'}
          </button>
        ) : (
          <p className="text-xs text-white/40 italic">Guía en preparación · pronto disponible</p>
        )}
      </div>

      {/* Tips below PDF */}
      <div>
        <h3 className="text-lg font-bold mb-4">Consejos Antes de Subir</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {guideTips.map((tip, i) => (
            <div key={i} className="glass-card p-4">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-[#E8720C]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[#E8720C] text-xs font-bold">{i + 1}</span>
                </div>
                <div>
                  <h4 className="font-semibold text-sm mb-1">{tip.title}</h4>
                  <p className="text-xs text-white/50 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monetization */}
      <div className="glass-card p-6">
        <h3 className="text-lg font-bold mb-4 text-center">Como Monetizar tu Musica</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-white/[0.03]">
            <h4 className="font-bold text-[#E8720C] text-sm mb-1">Streaming</h4>
            <p className="text-xs text-white/50">Spotify: ~$0.003-$0.005/stream. Apple Music: ~$0.007-$0.01/stream. 1M reproducciones = $3,000-$5,000.</p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03]">
            <h4 className="font-bold text-[#E8720C] text-sm mb-1">YouTube Content ID</h4>
            <p className="text-xs text-white/50">Si alguien usa tu beat en YouTube, tu recibes los ingresos publicitarios automaticamente.</p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03]">
            <h4 className="font-bold text-[#E8720C] text-sm mb-1">Sync Licensing</h4>
            <p className="text-xs text-white/50">Tu musica en TV, peliculas, videojuegos. Un sync puede pagar $500 hasta $50,000+.</p>
          </div>
          <div className="p-4 rounded-xl bg-white/[0.03]">
            <h4 className="font-bold text-[#E8720C] text-sm mb-1">Performance Royalties</h4>
            <p className="text-xs text-white/50">Radio, TV, bares, eventos. Registrate en ASCAP/BMI para cobrar estas regalias.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
